import { FeedbackButton } from "@/components/FeedbackButton";
import Menu from "@/components/Menu";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  AccountCircleIcon as AccountCircle,
  ArrowBackIcon as ArrowBack,
  BubbleChartIcon as BubbleChart,
  ChatBubbleOutlineRoundedIcon as ChatBubbleOutlineRounded,
  Typography,
} from "@iluvatar/ui-kit";

import { Link, useLocation } from "@tanstack/react-router";

import SubscriptionIcon from "@/assets/font-awesome/regular/credit-card.svg?react";
import SettingsIcon from "@/assets/font-awesome/regular/gear.svg?react";
import LogoutIcon from "@/assets/font-awesome/regular/right-from-bracket.svg?react";
import { FlexSpacer } from "@/components/FlexSpacer";
import { ImageAvatar } from "@/components/ImageRef";
import { NavLoadingIcon } from "@/components/NavLoadingIcon";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNotifications } from "@/hooks/useNotifications";
import { cn } from "@/utilities";

import { useAuthenticationContext } from "../contexts/authentication/authentication.context";

import { MEDIAQUERY } from "@iluvatar/global/src/constants";
import { IFileReference, ProfileDetails } from "@iluvatar/global/src/typings";

type PageInterface = React.PropsWithChildren & {
  menu?: React.ReactNode;
  drawerActionName?: string;
  drawerAction?: () => void;
  isSecondNav?: boolean;
};

function getProfileAv(
  prof?: ProfileDetails | undefined,
): IFileReference | undefined {
  if (prof?.imageIds[0]) {
    return (prof?.imagesById || {})[prof?.imageIds[0]];
  }

  return;
}

const PageNav: React.FC<PageInterface> = ({ children }) => {
  const { SUBSCRIPTIONS: subscriptionsEnabled } = useFeatureFlags();
  const isMobile = useMediaQuery(MEDIAQUERY.tablet);
  const location = useLocation();
  const { uid, isAuthenticated } = useAuthenticationContext();
  const { user } = useCurrentUser();
  const likes = useNotifications("likes");
  const matches = useNotifications("matches");
  const messages = useNotifications("messages");

  const isActive = (path: string, strict?: boolean): boolean => {
    if (strict) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={
          "nav [backdrop-filter:blur(16px)] z-40 [height:var(--nav-height)] [top:0] [left:0] [right:0] fixed [&_.container]:h-full [&_.container]:[max-width:var(--container-width)] [&_.container]:[margin:auto] [&_.container]:[padding:0_32px] [&_.container]:flex [&_.container]:flex-row [&_.container]:justify-start [&_.container]:[gap:16px] [&_.title]:flex [&_.title]:items-center [&_.title]:[border-bottom:1px_solid_transparent] [&_.title]:[height:var(--nav-height)] [&_.title]:[font-size:16px] [&_.title]:[font-weight:600] [&_.title]:[transition:opacity_0.25s] [&_.nav-link]:flex [&_.nav-link]:items-center [&_.nav-link]:[border-bottom:1px_solid_transparent] [&_.nav-link]:[height:var(--nav-height)] [&_.nav-link]:[font-size:16px] [&_.nav-link]:[font-weight:400] [&_.nav-link]:[transition:opacity_0.25s] [&_.nav-link.small]:[font-size:14px] [&_.nav-link:hover]:[color:var(--color-primary)] [&_.nav-link.active]:[color:var(--color-primary)] [&_.profile-active_img]:[outline:2px_solid_var(--color-primary)] [&_.profile-active_img]:[outline-offset:3px] [&.active]:[color:var(--color-primary)] [@media(max-width:_780px)]:[&_.container]:[padding:0_16px]"
        }
      >
        <div className="container">
          {isAuthenticated && isActive("/chat") ? (
            <Link
              to="/matches"
              preload="intent"
              aria-label="Back to Matches"
              className="title"
            >
              <NavLoadingIcon />
              <ArrowBack />
              <Typography variant="caption" className="ml-2 hidden sm:inline">
                Back to Matches
              </Typography>
            </Link>
          ) : (
            <Link
              to="/"
              preload={isAuthenticated ? "intent" : undefined}
              aria-label="Bubble home"
              className="title h-[62px] justify-center align-center"
            >
              <NavLoadingIcon />
              {!isMobile && "Bubble"}
            </Link>
          )}

          <FlexSpacer />
          {isAuthenticated && (
            <Link
              to="/likes"
              preload="intent"
              aria-label="Likes"
              className={cn("h-[100%] flex items-center nav-link relative", {
                active: isActive("/likes"),
              })}
            >
              {likes.hasUnread && (
                <div className="rounded bg-amber-500 w-[9px] h-[9px] absolute bottom-[17px] right-[-1px] z-[100]" />
              )}
              <BubbleChart className="w-[36px] h-[30px] mt-[-2px]" />
            </Link>
          )}
          {isAuthenticated && (
            <Link
              to="/matches"
              preload="intent"
              aria-label="Messages"
              className={cn(
                "h-[100%] flex items-center nav-link relative mr-1",
                {
                  active: isActive("/matches") || isActive("/chat"),
                },
              )}
            >
              {(matches.hasUnread || messages.hasUnread) && (
                <div className="rounded bg-amber-500 w-[9px] h-[9px] absolute bottom-[17px] right-[-1px] z-[100]" />
              )}
              <ChatBubbleOutlineRounded className="w-[25px] mb-[-3px] h-[27px]" />
              {/* <ChatBubble /> */}
              {/* <Diversity1 className="w-[35px] mb-[0px] h-[27px]" /> */}
            </Link>
          )}
          {isAuthenticated && (
            <>
              <FeedbackButton reporterId={uid!} neon size="sm" />
              <Menu
                label="User menu"
                trigger={
                  <button
                    type="button"
                    aria-label="User menu"
                    aria-current={
                      isActive(`/user/${uid}/edit`, true) ? "page" : undefined
                    }
                    className="flex items-center justify-center"
                  >
                    <ImageAvatar
                      communityId="default"
                      className="w-8 h-8"
                      imgRef={getProfileAv(user?.profiles.default)}
                    />
                  </button>
                }
                items={[
                  {
                    id: "profile",
                    label: "Profiles",
                    icon: <AccountCircle />,
                    render: (
                      <Link to="/user/$userId/edit" params={{ userId: uid! }} />
                    ),
                  },
                  {
                    id: "settings",
                    label: "Settings",
                    icon: <SettingsIcon />,
                    render: (
                      <Link
                        to="/user/$userId/account-settings"
                        params={{ userId: uid! }}
                      />
                    ),
                  },
                  ...(subscriptionsEnabled
                    ? [
                        {
                          id: "subscription",
                          label: "Subscription",
                          icon: <SubscriptionIcon />,
                          render: <Link to="/subscription" />,
                        },
                      ]
                    : []),
                  { id: "separator", label: "", separator: true },
                  {
                    id: "logout",
                    label: "Logout",
                    icon: <LogoutIcon />,
                    render: <Link to="/logout" />,
                  },
                ]}
              />
            </>
          )}
        </div>
      </header>
      <>{children}</>
    </>
  );
};

export default PageNav;
